import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom/cjs/react-router-dom.min';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import IMG_HERO from 'assets/images/email/hero-illustration-top.png';
import IMG_MARKETING from 'assets/images/email/marketing-illustration.png';
import IMG_RESULT from 'assets/images/email/result-illustration.png';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import UpgradePlanButton from 'components/hr/timeOffSplash/UpgradePlanButton.component';
import LINK from 'constants/link.const';
import PLAN_NAME from 'constants/plan.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import linkTo from 'utils/links/linkTo.util';
import C from 'utils/values/constant.util';

import CN from './ClientEmailsSplash.module.scss';

const getHeroBullets = (t) =>
  C([
    t('clientEmailSplash.hero.bullets.noDesignSkills'),
    t('clientEmailSplash.hero.bullets.customiseTemplates'),
    t('clientEmailSplash.hero.bullets.trackResults'),
    t('clientEmailSplash.hero.bullets.freeCampaign'),
  ]);

const getMarketingBullets = (t) =>
  C([
    t('clientEmailSplash.marketing.bullets.templates'),
    t('clientEmailSplash.marketing.bullets.customImages'),
    t('clientEmailSplash.marketing.bullets.branding'),
  ]);

const getResultsBullets = (t) =>
  C([
    t('clientEmailSplash.results.bullets.learnOffers'),
    t('clientEmailSplash.results.bullets.seeBookings'),
    t('clientEmailSplash.results.bullets.engagement'),
  ]);

const ClientEmailsSplash = ({ plan }) => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const heroBullets = useMemo(() => getHeroBullets(t), [t]);
  const marketingBullets = useMemo(() => getMarketingBullets(t), [t]);
  const resultsBullets = useMemo(() => getResultsBullets(t), [t]);

  const hasEmailAccess = plan === PLAN_NAME.advanced || plan === PLAN_NAME.supercharged;
  const emailsLink = linkTo({ pattern: LINK.emails, params: { salonId }, fault: '#' });

  return hasEmailAccess ? (
    <div data-bem="ClientEmailsSplash__just-activated-plan" className={CN.activatedTContainer}>
      <div className={CN.activatedTitle}>
        {S.partyingFace} {t('clientEmailSplash.activated.title')}
      </div>
      <div> {t('clientEmailSplash.activated.description')}</div>
      <NavLink to={emailsLink} className={CN.emailLink}>
        {t('clientEmailSplash.activated.cta')}
      </NavLink>
    </div>
  ) : (
    <div data-bem="ClientEmailsSplash" className={CN.component}>
      <div className={CN.wrap}>
        <div data-bem="ClientEmailsSplash__content-for-essential-salons">
          {/* Hero block */}
          <div className={CN.container} data-bem="ClientEmailsSplash__hero">
            <div>
              <div data-bem="ClientEmailsSplash__hero-badge" className={CN.badgeContainer}>
                <Badge variant="fine">
                  {t('clientEmailSplash.hero.badge')} {S.partyingFace}
                </Badge>
              </div>

              <div className={CN.blockTitle}>
                {t('clientEmailSplash.hero.title')} {S.email}
              </div>
              <Bullets data={heroBullets} />

              <div className={CN.upgradeButtonContainer} data-bem="ClientEmailsSplash__upgrade-button-container">
                <UpgradePlanButton text={t('clientEmailSplash.hero.upgradeButton')} />
              </div>
            </div>
            <div
              className={cn({
                [CN.illustration]: true,
                [CN.heroImg]: true,
              })}
            >
              <img src={IMG_HERO} alt="" className={CN.imgCard} />
            </div>
          </div>

          {/* Marketing block */}
          <div
            data-bem="ClientEmailsSplash__marketing"
            className={cn({
              [CN.container]: true,
              [CN.gradient]: true,
            })}
          >
            <div className={CN.marketingImgContainer}>
              <img src={IMG_MARKETING} alt="" className={CN.imgCard} />
            </div>
            <div className={CN.marketingTextContainer}>
              <div
                className={cn({
                  [CN.blockTitle]: true,
                  [CN.small]: true,
                })}
              >
                {t('clientEmailSplash.marketing.title')}
              </div>
              <div className={CN.subTitle}>{t('clientEmailSplash.marketing.subtitle')}</div>
              <Bullets data={marketingBullets} variantInvert checkmarkWhite />
            </div>
          </div>

          {/* Results block */}
          <div className={CN.container}>
            <div data-bem="ClientEmailsSplash__results">
              <div
                className={cn({
                  [CN.blockTitle]: true,
                  [CN.small]: true,
                })}
              >
                {t('clientEmailSplash.results.title')}
              </div>
              <Bullets data={resultsBullets} />
            </div>
            <div
              className={cn({
                [CN.illustration]: true,
                [CN.resultImg]: true,
              })}
            >
              <img src={IMG_RESULT} alt="" className={CN.imgCard} />
            </div>
          </div>

          <div
            className={cn({
              [CN.container]: true,
              [CN.reverseOrder]: true,
            })}
            data-bem="ClientEmailsSplash__upgrade"
          >
            <div className={CN.table} data-bem="ClientEmailsSplash__table">
              <div className={CN.header}>
                <div className={CN.headerEmpty} />
                <div className={CN.headerItem}>
                  <Icon variant="label" width="lg" />
                  {t('clientEmailSplash.upgrade.table.essential')}
                </div>
                <div
                  className={cn({
                    [CN.headerItem]: true,
                    [CN.headerItemAdvanced]: true,
                  })}
                >
                  <Icon variant="emptyStar" width="lg" />
                  {t('clientEmailSplash.upgrade.table.advanced')}
                </div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}> {t('clientEmailSplash.upgrade.table.rows.appointments')}</div>
                <div className={CN.cell}>
                  <Icon variant="off" width="lg" />
                </div>
                <div className={CN.cell}>
                  <Icon variant="on" width="lg" />
                </div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}>{t('clientEmailSplash.upgrade.table.rows.sms')}</div>
                <div className={CN.cellBold}>50</div>
                <div className={CN.cellBold}>500</div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}>{t('clientEmailSplash.upgrade.table.rows.emailMarketing')}</div>
                <div className={CN.cell}>
                  <Icon variant="off" width="lg" />
                </div>
                <div className={CN.cell}>
                  <Icon variant="on" width="lg" />
                </div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}>{t('clientEmailSplash.upgrade.table.rows.holidayTracking')}</div>
                <div className={CN.cell}>
                  <Icon variant="off" width="lg" />
                </div>
                <div className={CN.cell}>
                  <Icon variant="on" width="lg" />
                </div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}>{t('clientEmailSplash.upgrade.table.rows.clientTags')}</div>
                <div className={CN.cell}>
                  <Icon variant="off" width="lg" />
                </div>
                <div className={CN.cell}>
                  <Icon variant="on" width="lg" />
                </div>
              </div>

              <div className={CN.row}>
                <div className={CN.cell}>{t('clientEmailSplash.upgrade.table.rows.splitPayments')}</div>
                <div className={CN.cell}>
                  <Icon variant="off" width="lg" />
                </div>
                <div className={CN.cell}>
                  <Icon variant="on" width="lg" />
                </div>
              </div>
            </div>

            <div className={CN.upgradeTextContainer}>
              <div
                className={cn({
                  [CN.blockTitle]: true,
                  [CN.small]: true,
                })}
              >
                {t('clientEmailSplash.upgrade.title')}
              </div>
              <div className={CN.infoText}>{t('clientEmailSplash.upgrade.description1')}</div>
              <div className={CN.infoText}>
                {t('clientEmailSplash.upgrade.description2')}
                <span className={CN.extraBold}> 15%</span>
              </div>
              <div className={CN.upgradeButtonContainer} data-bem="ClientEmailsSplash__upgrade-button-container">
                <UpgradePlanButton text={t('clientEmailSplash.upgrade.upgradeButton')} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientEmailsSplashHoc = connect((state) => ({
  plan: selectCurrentSalonPricingPlan(state),
}))(ClientEmailsSplash);

export default ClientEmailsSplashHoc;
